// frontend/hooks/useSidebarDragDrop.ts

import { useState, useRef } from "react";
import { generateDocId } from "../utils/documentUtils";
import type { DropTarget, DropWhere } from "../contexts/SidebarContext";
import { authHeaders } from "../utils/api";

interface UseSidebarDragDropProps {
  documents: any[];
  user: any;
  apiUrl: string;
  fetchDocuments: () => void;
  setExpandedDocs: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
}

export const useSidebarDragDrop = ({ documents, user, apiUrl, fetchDocuments, setExpandedDocs }: UseSidebarDragDropProps) => {
  const [dropTarget, setDropTarget] = useState<DropTarget | null>(null);
  const isTransferring = useRef(false);

  const [transferModal, setTransferModal] = useState<{
    isOpen: boolean;
    type: 'TEAM_TO_PERSONAL' | 'PERSONAL_TO_TEAM' | null;
    payload: { draggedDocId: string; targetParentId: string | null; targetWorkspaceType: string; draggedDoc: any } | null;
  }>({ isOpen: false, type: null, payload: null });

  const handleDragStart = (e: React.DragEvent, docId: string, isFavorite: boolean = false) => {
    if (isFavorite) {
      e.preventDefault();
      return alert("즐겨찾기 항목은 이동할 수 없습니다.");
    }
    const doc = documents.find(d => d.id === docId);
    if (!doc) return;
    e.dataTransfer.setData("docId", doc.id);
    e.dataTransfer.setData("docTitle", doc.title);
    if (doc.icon) e.dataTransfer.setData("docIcon", doc.icon);
  };

  const handleDragOver = (e: React.DragEvent, targetId: string | null, where: DropWhere = "inside") => {
    e.preventDefault();
    if (targetId === null) {
      setDropTarget(null);
      return;
    }
    setDropTarget({ id: targetId, where });
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDropTarget(null);
  };

  // ── 재정렬 (before / after 드롭) ─────────────────────────────────────────

  const handleReorder = async (e: React.DragEvent, targetId: string, where: "before" | "after") => {
    e.preventDefault();
    setDropTarget(null);

    const draggedDocId = e.dataTransfer.getData("docId");
    if (!draggedDocId || draggedDocId === targetId) return;

    const draggedDoc = documents.find(d => d.id === draggedDocId);
    const targetDoc  = documents.find(d => d.id === targetId);
    if (!draggedDoc || !targetDoc) return;

    // 워크스페이스 타입이 다르면 기존 MOVE 흐름으로 위임
    if (draggedDoc.workspaceType !== targetDoc.workspaceType) {
      await executeTransfer("MOVE", draggedDocId, targetDoc.parentId, targetDoc.workspaceType, draggedDoc);
      return;
    }

    if (isTransferring.current) return;
    isTransferring.current = true;

    try {
      // 같은 부모의 형제들을 position 순으로 정렬
      const siblings = documents
        .filter(d => d.parentId === targetDoc.parentId && d.workspaceType === targetDoc.workspaceType && !d.isDeleted)
        .sort((a, b) => (a.position ?? 0) - (b.position ?? 0) || new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

      const targetIdx = siblings.findIndex(d => d.id === targetId);

      // where === 'before': draggedDoc를 targetDoc 앞에 삽입 → beforeId = target의 앞 형제, afterId = target
      // where === 'after' : draggedDoc를 targetDoc 뒤에 삽입 → beforeId = target, afterId = target의 뒤 형제
      let beforeId: string | null = null;
      let afterId:  string | null = null;

      if (where === "before") {
        afterId  = targetId;
        const prevSibling = siblings.slice(0, targetIdx).filter(d => d.id !== draggedDocId).at(-1);
        beforeId = prevSibling?.id ?? null;
      } else {
        beforeId = targetId;
        const nextSibling = siblings.slice(targetIdx + 1).find(d => d.id !== draggedDocId);
        afterId  = nextSibling?.id ?? null;
      }

      const newParentId = targetDoc.parentId !== draggedDoc.parentId ? targetDoc.parentId : undefined;

      const res = await fetch(`${apiUrl}/documents/${draggedDocId}/reorder`, {
        method: "PATCH",
        headers: authHeaders(user?.accessToken),
        body: JSON.stringify({ beforeId, afterId, ...(newParentId !== undefined && { newParentId }) })
      });

      if (!res.ok) throw new Error((await res.json()).error || "순서 변경에 실패했습니다.");

      // 부모 문서가 열려 있다면 본문 pageLink 블록 순서도 동기화
      window.dispatchEvent(new CustomEvent("child-doc-block-reorder", {
        detail: { movedDocId: draggedDocId, targetDocId: targetId, where, parentId: targetDoc.parentId ?? null }
      }));
      fetchDocuments();
    } catch (err: any) {
      alert(err.message || "네트워크 오류로 처리하지 못했습니다.");
    } finally {
      isTransferring.current = false;
    }
  };

  // ── 기존 부모 변경 드롭 (inside) ─────────────────────────────────────────

  const handleDrop = async (e: React.DragEvent, targetParentId: string | null, targetWorkspaceType: string, isSharedSection: boolean = false) => {
    e.preventDefault();
    setDropTarget(null);

    const draggedDocId = e.dataTransfer.getData("docId");
    if (!draggedDocId || draggedDocId === targetParentId) return;

    const draggedDoc = documents.find(d => d.id === draggedDocId);
    if (!draggedDoc) return;
    if (draggedDoc.parentId === targetParentId && draggedDoc.workspaceType === targetWorkspaceType) return;

    let currentParent = targetParentId;
    while (currentParent) {
      if (currentParent === draggedDocId) {
        alert("상위 폴더를 자신의 하위 폴더로 이동할 수 없습니다.");
        return;
      }
      const parentDoc = documents.find(d => d.id === currentParent);
      currentParent = parentDoc ? parentDoc.parentId : null;
    }

    const isOriginShared = draggedDoc.workspaceType === "TEAM" && draggedDoc.accessDept?.split(',')[0]?.trim() !== user?.department;

    let finalWorkspaceType = targetWorkspaceType;
    let isTargetShared = isSharedSection;
    if (targetParentId && targetParentId !== "TEAM_ROOT" && targetParentId !== "PERSONAL_ROOT") {
      const parentDoc = documents.find(d => d.id === targetParentId);
      if (parentDoc) {
        finalWorkspaceType = parentDoc.workspaceType;
        if (parentDoc.workspaceType === "TEAM" && parentDoc.accessDept?.split(',')[0]?.trim() !== user?.department) {
          isTargetShared = true;
        }
      }
    }

    if (isOriginShared) return alert("공유 스페이스의 문서는 다른 곳으로 이동하거나 복사할 수 없습니다.");
    if (isTargetShared) return alert("공유 스페이스로는 문서를 이동하거나 복사할 수 없습니다.");

    if (draggedDoc.workspaceType === "TEAM" && finalWorkspaceType === "PERSONAL") {
      setTransferModal({ isOpen: true, type: 'TEAM_TO_PERSONAL', payload: { draggedDocId, targetParentId, targetWorkspaceType: finalWorkspaceType, draggedDoc } });
      return;
    }
    if (draggedDoc.workspaceType === "PERSONAL" && finalWorkspaceType === "TEAM") {
      setTransferModal({ isOpen: true, type: 'PERSONAL_TO_TEAM', payload: { draggedDocId, targetParentId, targetWorkspaceType: finalWorkspaceType, draggedDoc } });
      return;
    }

    await executeTransfer("MOVE", draggedDocId, targetParentId, finalWorkspaceType, draggedDoc);
  };

  const executeTransfer = async (actionType: "MOVE" | "COPY", draggedDocId: string, targetParentId: string | null, targetWorkspaceType: string, draggedDoc: any) => {
    if (isTransferring.current) return;
    isTransferring.current = true;

    try {
      const cleanParentId = (targetParentId === "TEAM_ROOT" || targetParentId === "PERSONAL_ROOT") ? null : targetParentId;

      if (actionType === "MOVE") {
        const res = await fetch(`${apiUrl}/documents/${draggedDocId}`, {
          method: "PATCH",
          headers: authHeaders(user?.accessToken),
          body: JSON.stringify({
            parentId: cleanParentId,
            workspaceType: targetWorkspaceType,
            requesterRole: user?.role,
            requesterDept: user?.department,
            requesterId: user?.id
          })
        });
        if (!res.ok) throw new Error((await res.json()).message || "문서 이동 권한이 없습니다.");

        const oldParentId = draggedDoc.parentId;
        if (cleanParentId) setExpandedDocs(prev => ({ ...prev, [cleanParentId]: true }));
        if (oldParentId) window.dispatchEvent(new CustomEvent("remove-child-block", { detail: { parentId: oldParentId, childId: draggedDocId } }));
        if (cleanParentId) {
          window.dispatchEvent(new CustomEvent("insert-child-block", {
            detail: { parentId: cleanParentId, childId: draggedDocId, childTitle: draggedDoc.title, childIcon: draggedDoc.icon || "" }
          }));
        }
      } else if (actionType === "COPY") {
        const newId = generateDocId();
        const res = await fetch(`${apiUrl}/documents`, {
          method: "POST",
          headers: authHeaders(user?.accessToken),
          body: JSON.stringify({
            id: newId,
            title: draggedDoc.title,
            workspaceType: targetWorkspaceType,
            ownerId: user?.id,
            accessDept: targetWorkspaceType === "TEAM" ? user?.department : null,
            isGuestAllowed: false,
            requesterRole: user?.role,
            requesterDept: user?.department,
            parentId: cleanParentId,
            userName: user?.name,
            sourceDocId: draggedDocId
          })
        });
        if (!res.ok) throw new Error((await res.json()).message || "문서 복사 중 오류가 발생했습니다.");
        if (cleanParentId) setExpandedDocs(prev => ({ ...prev, [cleanParentId]: true }));
      }

      fetchDocuments();
      window.dispatchEvent(new Event("force-sidebar-refresh"));
    } catch (error: any) {
      alert(error.message || "네트워크 오류로 처리하지 못했습니다.");
    } finally {
      setTransferModal({ isOpen: false, type: null, payload: null });
      isTransferring.current = false;
    }
  };

  // 하위 호환: dragOverId는 inside 하이라이트용
  const dragOverId = dropTarget?.where === "inside" ? dropTarget.id : null;

  return {
    dropTarget,
    dragOverId,
    handleDragStart,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleReorder,
    transferModal,
    setTransferModal,
    executeTransfer
  };
};
